<template>
  <div class="order-reconciliation-page">
    <b-row>
      <b-col md="12" lg="12" sm="12">
        <KTCodePreview>
          <template v-slot:preview>
            <div class="order-reconciliation-page__header mb-4">
              <div class="d-flex flex-wrap justify-content-between align-items-start">
                <div class="order-reconciliation-page__title-block pr-3">
                  <h4 class="order-reconciliation-page__title mb-1">
                    Đối soát đơn hàng Shopee
                  </h4>
                  <p class="order-reconciliation-page__subtitle text-muted mb-0 small">
                    Theo dõi phí sàn và số tiền thực thu theo kỳ
                  </p>
                </div>
                <b-button
                  variant="light"
                  class="order-reconciliation-page__back-btn border"
                  size="sm"
                  @click="navigateBack"
                >
                  <i class="flaticon2-back mr-1"></i> Quay lại
                </b-button>
              </div>
            </div>

            <b-card class="order-reconciliation-page__filter mb-4" body-class="order-reconciliation-page__filter-body">
              <h6 class="text-muted mb-3">Bộ lọc</h6>
              <b-row align-v="end">
                <b-col cols="12" sm="6" lg="3" class="mb-3 mb-lg-0">
                  <b-form-group label="Gian hàng" label-for="connection-select" class="mb-0">
                    <b-form-select
                      id="connection-select"
                      class="select-style"
                      v-model="selectedConnectionId"
                      :options="connectionOptions"
                      :disabled="loadingConnections"
                      @change="handleConnectionChange"
                      size="sm"
                    >
                      <template #first>
                        <b-form-select-option :value="null">-- Chọn gian hàng --</b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                </b-col>
                <b-col cols="6" sm="3" lg="2" class="mb-3 mb-lg-0">
                  <b-form-group label="Từ ngày" label-for="from-date" class="mb-0">
                    <date-picker
                      id="from-date"
                      v-model="filters.fromDate"
                      :config="dpConfigs.date"
                      class="form-control form-control-sm order-log-filter__date-input"
                    ></date-picker>
                  </b-form-group>
                </b-col>
                <b-col cols="6" sm="3" lg="2" class="mb-3 mb-lg-0">
                  <b-form-group label="Đến ngày" label-for="to-date" class="mb-0">
                    <date-picker
                      id="to-date"
                      v-model="filters.toDate"
                      :config="dpConfigs.date"
                      class="form-control form-control-sm order-log-filter__date-input"
                    ></date-picker>
                  </b-form-group>
                </b-col>
                <b-col cols="12" lg="auto" class="ml-lg-auto">
                  <div class="d-flex flex-wrap justify-content-lg-end">
                    <b-button
                      size="sm"
                      variant="primary"
                      class="order-reconciliation-page__filter-btn mr-2 mb-2 mb-sm-0"
                      @click="handleApplyFilter"
                      :disabled="!selectedConnectionId"
                    >
                      <i class="flaticon2-search-1 mr-1"></i> Lọc
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="order-reconciliation-page__filter-btn mb-2 mb-sm-0 mr-2"
                      @click="handleClearFilter"
                      :disabled="!selectedConnectionId"
                    >
                      <i class="flaticon2-refresh mr-1"></i> Xóa lọc
                    </b-button>
                    <b-button
                      size="sm"
                      variant="success"
                      class="order-reconciliation-page__filter-btn mb-2 mb-sm-0"
                      :disabled="!selectedConnectionId || exportingExcel"
                      @click="handleExportExcel"
                    >
                      <b-spinner v-if="exportingExcel" small class="mr-1"></b-spinner>
                      <i v-else class="flaticon2-download mr-1"></i>
                      {{ exportingExcel ? 'Đang xuất...' : 'Xuất Excel' }}
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <div v-if="!selectedConnectionId" class="order-reconciliation-page__empty">
              <div class="text-center py-5">
                <i class="flaticon2-checking icon-3x text-muted mb-3"></i>
                <h4 class="text-muted mb-3">Vui lòng chọn gian hàng để xem đối soát</h4>
              </div>
            </div>

            <div v-else-if="!loading && items.length === 0" class="order-reconciliation-page__empty">
              <div class="text-center py-5">
                <i class="flaticon2-checking icon-3x text-muted mb-3"></i>
                <h4 class="text-muted mb-3">Không có dữ liệu đối soát trong khoảng thời gian đã chọn</h4>
              </div>
            </div>

            <template v-else>
              <div class="order-reconciliation-page__results">
                <div id="reconciliation-scroll-table">
                  <b-table
                    :items="items"
                    :fields="computedTableFields"
                    :busy="loading"
                    striped
                    hover
                    responsive
                    show-empty
                    empty-text="Không có dữ liệu"
                    thead-class="reconciliation-thead"
                    table-class="reconciliation-table-inner"
                    class="reconciliation-table-host mb-0"
                  >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong class="ml-2">Đang tải...</strong>
                  </div>
                </template>
                <template #cell(externalOrderId)="data">
                  <span class="font-weight-bold">{{ data.value }}</span>
                </template>
                <template #cell(orderId)="data">
                  <router-link
                    v-if="data.value"
                    :to="{ name: 'edit-order', params: { id: data.value } }"
                    class="order-id-link"
                  >
                    {{ data.value }}
                  </router-link>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(orderDate)="data">
                  {{ formatDate(data.value) }}
                </template>
                <template #cell(lineItemsSummary)="data">
                  <span class="reconciliation-line-items small">{{ data.value || '—' }}</span>
                </template>
                <template #cell(orderStatusLabel)="data">
                  {{ data.value || '—' }}
                </template>
                <template #cell(invoiceAmount)="data">
                  <span
                    class="text-right d-block reconciliation-num"
                    :class="{ 'reconciliation-zero': isZeroAmount(data.value) }"
                  >{{ convertPrice(data.value) }}</span>
                </template>
                <template #cell(totalFees)="data">
                  <span
                    class="text-right d-block reconciliation-num font-weight-bold"
                    :class="{ 'reconciliation-zero': isZeroAmount(data.value) }"
                  >{{ convertPrice(data.value) }}</span>
                </template>
                <template #cell(actualReceived)="data">
                  <span
                    class="text-right d-block reconciliation-num font-weight-bold reconciliation-actual"
                    :class="{ 'reconciliation-zero': isZeroAmount(data.value) }"
                  >{{ convertPrice(data.value) }}</span>
                </template>
                  </b-table>
                </div>

              <b-card
                v-if="items.length > 0"
                no-body
                class="mt-4 order-reconciliation-page__summary"
              >
                <template v-if="summary">
                  <div class="order-reconciliation-page__summary-head">
                    <span class="order-reconciliation-page__summary-title">Tổng kết kỳ</span>
                  </div>
                  <div class="order-reconciliation-page__summary-grid">
                    <div class="order-reconciliation-page__summary-item">
                      <span class="order-reconciliation-page__summary-label">Số tiền hóa đơn</span>
                      <span class="order-reconciliation-page__summary-value">{{ convertPrice(summary.invoiceAmount) }}</span>
                    </div>
                    <div class="order-reconciliation-page__summary-item">
                      <span class="order-reconciliation-page__summary-label">Tổng phí</span>
                      <span class="order-reconciliation-page__summary-value">{{ convertPrice(summary.totalFees) }}</span>
                    </div>
                    <div class="order-reconciliation-page__summary-item">
                      <span class="order-reconciliation-page__summary-label">Số tiền thực thu</span>
                      <span class="order-reconciliation-page__summary-value order-reconciliation-page__summary-value--accent">{{ convertPrice(summary.actualReceived) }}</span>
                    </div>
                    <div class="order-reconciliation-page__summary-item">
                      <span class="order-reconciliation-page__summary-label">Số đơn</span>
                      <span class="order-reconciliation-page__summary-value">{{ summary.count || items.length }}</span>
                    </div>
                  </div>
                </template>
                <div
                  v-if="voucherColumnDefs.length"
                  class="order-reconciliation-page__summary-voucher px-3 pb-3 pt-1"
                  :class="{ 'border-top': !!summary }"
                >
                  <div
                    v-if="!summary"
                    class="order-reconciliation-page__summary-head"
                  >
                    <span class="order-reconciliation-page__summary-title">Voucher</span>
                  </div>
                  <span
                    class="order-reconciliation-page__summary-title d-block mb-2 small text-muted"
                    :class="{ 'mt-2': summary }"
                  >
                    {{ summary ? 'Tổng voucher theo các đơn trên trang' : 'Theo các đơn trên trang hiện tại' }}
                  </span>
                  <div class="order-reconciliation-page__summary-voucher-grid">
                    <div
                      v-for="col in voucherColumnDefs"
                      :key="col.key"
                      class="order-reconciliation-page__summary-item py-2"
                    >
                      <span class="order-reconciliation-page__summary-label">{{ col.label }}</span>
                      <span
                        class="order-reconciliation-page__summary-value"
                        :class="{
                          'text-muted font-weight-normal':
                            isZeroAmount(voucherPeriodTotals[col.key]),
                        }"
                      >
                        {{ convertPrice(voucherPeriodTotals[col.key]) }}
                      </span>
                    </div>
                  </div>
                </div>
              </b-card>

              <b-row v-if="items.length > 0" class="mt-4 align-items-center">
                <b-col cols="12" md="auto">
                  <p class="order-reconciliation-page__total-hint text-muted mb-2 mb-md-0 small">
                    Hiển thị {{ items.length }} / {{ pagination.total }} bản ghi
                  </p>
                </b-col>
                <b-col>
                  <b-pagination
                    v-if="numberOfPage >= 2"
                    v-model="pagination.page"
                    class="custom-pagination"
                    :total-rows="pagination.total"
                    :per-page="pagination.limit"
                    @change="onPageChange"
                    align="right"
                    first-class="page-item-first btn btn-icon btn-sm m-1"
                    prev-class="page-item-prev btn btn-icon btn-sm m-1"
                    next-class="page-item-next btn btn-icon btn-sm m-1"
                    last-class="page-item-last btn btn-icon btn-sm m-1"
                    page-class="btn btn-icon btn-sm border-0 m-1"
                  >
                    <template v-slot:first-text>
                      <span><i class="ki ki-double-arrow-back icon-xs"></i></span>
                    </template>
                    <template v-slot:prev-text>
                      <i class="ki ki-arrow-back icon-xs"></i>
                    </template>
                    <template v-slot:next-text>
                      <i class="ki ki-arrow-next icon-xs"></i>
                    </template>
                    <template v-slot:last-text>
                      <span class="text-info">
                        <i class="ki ki-double-arrow-next icon-xs"></i>
                      </span>
                    </template>
                  </b-pagination>
                </b-col>
              </b-row>
              </div>
            </template>
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';
import { getToastInstance } from '@/utils/toastHelper';
import { convertPrice } from '@/utils/common';
import moment from 'moment';
import { getStartOfDay, getEndOfDay, DP_CONFIG } from '@/utils/date';
import fileDownload from '@/utils/file-download';

const shopeeAdminRepo = RepositoryFactory.get('shopeeAdmin');
const platformRepo = RepositoryFactory.get('platformIntegration');
const toast = getToastInstance();

function toNum(v) {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

export default {
  name: 'OrderReconciliationPage',
  components: {
    KTCodePreview,
  },
  data() {
    return {
      items: [],
      loading: true,
      filters: {
        fromDate: null,
        toDate: null,
      },
      pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 1,
      },
      connections: [],
      selectedConnectionId: null,
      loadingConnections: false,
      voucherColumnDefs: [
        { key: 'voucherFromShopee', label: 'Voucher Shopee' },
        { key: 'voucherFromSeller', label: 'Voucher người bán' },
        { key: 'voucherAmount', label: 'Tổng voucher' },
      ],
      feeColumnDefs: [
        { key: 'commissionFee', label: 'Phí cố định' },
        { key: 'serviceFee', label: 'Phí dịch vụ' },
        { key: 'sellerTransactionFee', label: 'Phí thanh toán' },
        { key: 'customerShipFee', label: 'Phí VC khách trả' },
        { key: 'finalShippingFee', label: 'Phí VC thực tế' },
        { key: 'reverseShippingFee', label: 'Phí VC trợ giá Shopee' },
        { key: 'buyerTransactionFee', label: 'Phí thanh toán bên mua' },
      ],
      summary: null,
      exportingExcel: false,
      dpConfigs: DP_CONFIG,
    };
  },
  computed: {
    connectionOptions() {
      return this.connections.map((c) => ({
        value: c.id,
        text: c.shopName,
      }));
    },
    numberOfPage() {
      return Math.ceil(this.pagination.total / this.pagination.limit) || 1;
    },
    /** Tổng voucher theo kỳ (cộng theo các cột đang hiển thị) */
    voucherPeriodTotals() {
      const acc = {};
      this.voucherColumnDefs.forEach((c) => {
        acc[c.key] = 0;
      });
      this.items.forEach((row) => {
        this.voucherColumnDefs.forEach((c) => {
          acc[c.key] += toNum(row[c.key]);
        });
      });
      return acc;
    },
    computedTableFields() {
      const feeTdClass = (value) => {
        const n = toNum(value);
        return [
          'text-right',
          'align-middle',
          'reconciliation-num',
          n === 0 ? 'reconciliation-zero' : null,
        ].filter(Boolean);
      };
      const base = [
        {
          key: 'externalOrderId',
          label: 'Mã đơn sàn',
          thStyle: { minWidth: '128px' },
          thClass: 'reconciliation-th',
        },
        {
          key: 'orderId',
          label: 'ID đơn hàng',
          thStyle: { minWidth: '96px' },
          thClass: 'reconciliation-th',
        },
        {
          key: 'orderDate',
          label: 'Ngày đơn',
          thStyle: { minWidth: '104px' },
          thClass: 'reconciliation-th',
        },
        {
          key: 'lineItemsSummary',
          label: 'Hàng hóa (SL)',
          thStyle: { minWidth: '220px', maxWidth: '280px' },
          thClass: 'reconciliation-th reconciliation-th--wrap',
          tdClass: 'reconciliation-td--wrap',
        },
        {
          key: 'orderStatusLabel',
          label: 'Trạng thái đơn',
          thStyle: { minWidth: '120px' },
          thClass: 'reconciliation-th',
        },
        {
          key: 'invoiceAmount',
          label: 'Số tiền hóa đơn',
          thStyle: { minWidth: '132px', textAlign: 'right' },
          thClass: 'reconciliation-th text-right',
          tdClass: 'text-right',
        },
      ];
      const voucherCols = this.voucherColumnDefs.map((f) => ({
        key: f.key,
        label: f.label,
        thStyle: { minWidth: '128px', textAlign: 'right' },
        thClass: 'reconciliation-th text-right',
        tdClass: feeTdClass,
        formatter: (v) => this.formatFee(v),
      }));
      const feeCols = this.feeColumnDefs.map((f) => ({
        key: f.key,
        label: f.label,
        thStyle: { minWidth: '132px', textAlign: 'right' },
        thClass: 'reconciliation-th text-right',
        tdClass: feeTdClass,
        formatter: (v) => this.formatFee(v),
      }));
      return [
        ...base,
        ...voucherCols,
        ...feeCols,
        {
          key: 'totalFees',
          label: 'Tổng phí',
          thStyle: { minWidth: '120px', textAlign: 'right' },
          thClass: 'reconciliation-th text-right',
          tdClass: (value) => [...feeTdClass(value), 'font-weight-bold'],
        },
        {
          key: 'actualReceived',
          label: 'Số tiền thực thu',
          thStyle: { minWidth: '140px', textAlign: 'right' },
          thClass: 'reconciliation-th text-right',
          tdClass: (value) => [
            ...feeTdClass(value),
            'font-weight-bold',
            'reconciliation-actual-cell',
          ],
        },
      ];
    },
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        this.restoreFromQuery(newQuery);
        this.fetchData();
      },
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kênh bán', route: '/platform-integration' },
      { title: 'Đối soát đơn hàng Shopee' },
    ]);
    this.fetchConnections();
    this.loadQueryParams();
  },
  methods: {
    convertPrice,
    isZeroAmount(value) {
      return toNum(value) === 0;
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY');
    },
    formatFee(value) {
      if (value === null || value === undefined) return '-';
      return convertPrice(value);
    },
    normalizeReconciliationRow(row) {
      const orderInfo = row.orderInfo || {};
      const fees = row.fees || {};
      const voucherColumnsMeta = row.voucherColumns || {};
      const invoiceAmount = toNum(row.invoiceAmount ?? row.totalAmount ?? orderInfo.totalAmount);
      const totalFees = toNum(row.totalFees ?? fees.total);
      const actualReceived = toNum(row.actualReceived ?? row.escrowAmount);
      const feeValues = {};
      this.feeColumnDefs.forEach((col) => {
        feeValues[col.key] = toNum(fees[col.key] ?? row[col.key]);
      });
      const voucherValues = {};
      this.voucherColumnDefs.forEach((col) => {
        const meta = voucherColumnsMeta[col.key];
        voucherValues[col.key] = toNum(row[col.key] ?? meta?.value);
      });
      const computedTotalFees =
        totalFees > 0 ? totalFees : Object.values(feeValues).reduce((s, v) => s + v, 0);
      const computedActual =
        actualReceived !== 0 ? actualReceived : invoiceAmount - computedTotalFees;
      return {
        externalOrderId: orderInfo.externalOrderId ?? row.externalOrderId,
        orderId: orderInfo.orderId ?? row.orderId,
        orderDate: orderInfo.orderDate ?? row.orderDate ?? row.createdAt,
        lineItemsSummary: row.lineItemsSummary ?? '—',
        orderStatusLabel:
          row.orderStatusLabel ??
          row.externalStatusLabel ??
          orderInfo.externalStatusLabel ??
          row.externalStatus ??
          '—',
        invoiceAmount,
        ...voucherValues,
        ...feeValues,
        totalFees: computedTotalFees,
        actualReceived: computedActual,
      };
    },
    async fetchConnections() {
      try {
        this.loadingConnections = true;
        const platform = this.$route.query.platform || 'shopee';
        const response = await platformRepo.getConfigs({ platform });
        this.connections = (response.items || []).filter((c) => c.status === 'connected');
        if (this.connections.length === 1 && !this.$route.query.connectionId) {
          this.selectedConnectionId = this.connections[0].id;
        }
      } catch (error) {
        console.error('Error fetching connections:', error);
        toast.makeToastFailure('Không thể tải danh sách kết nối');
      } finally {
        this.loadingConnections = false;
      }
    },
    async fetchData() {
      if (!this.selectedConnectionId) {
        this.items = [];
        this.loading = false;
        return;
      }
      try {
        this.loading = true;
        const params = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          connectionId: this.selectedConnectionId,
          status: 'synced',
        };
        if (this.filters.fromDate) {
          params.fromDate = getStartOfDay(this.filters.fromDate);
        }
        if (this.filters.toDate) {
          params.toDate = getEndOfDay(this.filters.toDate);
        }

        let response;
        try {
          response = await shopeeAdminRepo.getOrderReconciliation(params);
        } catch (e) {
          response = await shopeeAdminRepo.getOrderLogs(params);
          const raw = response.data || [];
          response = {
            data: {
              rows: raw.map((item) => ({
                ...item,
                orderInfo:
                  item.orderInfo ||
                  ({
                    externalOrderId: item.externalOrderId,
                    orderId: item.orderId,
                    orderDate: item.orderDate ?? item.createdAt,
                  }),
                invoiceAmount: item.invoiceAmount ?? item.totalAmount,
                fees:
                  item.fees ??
                  {
                    customerShipFee: item.customerShipFee,
                    finalShippingFee: item.finalShippingFee,
                    reverseShippingFee: item.reverseShippingFee,
                    commissionFee: item.commissionFee,
                    serviceFee: item.serviceFee,
                    sellerTransactionFee: item.sellerTransactionFee,
                    buyerTransactionFee: item.buyerTransactionFee,
                  },
              })),
            },
            pagination: response.pagination,
          };
        }

        const data = response.data || response;
        const rows = data.rows || data.data || [];
        const apiFeeColumnDefs = data.feeColumnDefs || [];
        let apiVoucherColumnDefs = data.voucherColumnDefs || [];

        if (!apiVoucherColumnDefs.length && rows.length > 0) {
          const vc = rows[0].voucherColumns;
          if (vc && typeof vc === 'object') {
            const keys = Object.keys(vc);
            if (keys.length) {
              apiVoucherColumnDefs = keys.map((key) => ({
                key,
                label: vc[key]?.label || key,
              }));
            }
          }
        }

        if (apiFeeColumnDefs.length > 0) {
          this.feeColumnDefs = apiFeeColumnDefs;
        }
        if (apiVoucherColumnDefs.length > 0) {
          this.voucherColumnDefs = apiVoucherColumnDefs;
        }

        this.summary = data.summary || null;

        this.items = rows.map((row) => this.normalizeReconciliationRow(row));

        const pag = response.pagination || data.pagination || {};
        if (pag.total !== undefined) {
          this.pagination = {
            page: pag.page ?? this.pagination.page,
            limit: pag.pageSize ?? pag.limit ?? this.pagination.limit,
            total: pag.total,
            totalPages: pag.totalPages ?? (Math.ceil((pag.total || 0) / (pag.pageSize || pag.limit || 50)) || 1),
          };
        }
      } catch (error) {
        console.error('Error fetching reconciliation:', error);
        toast.makeToastFailure('Không thể tải dữ liệu đối soát');
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    loadQueryParams() {
      this.restoreFromQuery(this.$route.query);
      this.fetchData();
    },
    restoreFromQuery(query) {
      if (query.connectionId !== undefined) {
        const conn = this.connections.find((c) => String(c.id) === String(query.connectionId));
        this.selectedConnectionId = conn ? conn.id : null;
      }
      this.filters.fromDate =
        query.fromDate && moment(query.fromDate, 'YYYY-MM-DD').isValid()
          ? moment(query.fromDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : null;
      this.filters.toDate =
        query.toDate && moment(query.toDate, 'YYYY-MM-DD').isValid()
          ? moment(query.toDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
          : null;
      if (query.page) {
        const page = parseInt(query.page, 10);
        this.pagination.page = isNaN(page) ? 1 : page;
      } else {
        this.pagination.page = 1;
      }
    },
    updateQueryParams() {
      const query = {};
      const platform = this.$route.query.platform || 'shopee';
      query.platform = platform;
      if (this.selectedConnectionId) {
        query.connectionId = this.selectedConnectionId;
      }
      if (this.filters.fromDate) {
        const d = moment(this.filters.fromDate, 'DD/MM/YYYY');
        if (d.isValid()) query.fromDate = d.format('YYYY-MM-DD');
      }
      if (this.filters.toDate) {
        const d = moment(this.filters.toDate, 'DD/MM/YYYY');
        if (d.isValid()) query.toDate = d.format('YYYY-MM-DD');
      }
      if (this.pagination.page > 1) {
        query.page = this.pagination.page;
      }
      this.$router.push({
        name: 'platform-integration-reconciliation',
        query,
      }).catch(() => {});
    },
    handleConnectionChange() {
      this.pagination.page = 1;
      this.updateQueryParams();
    },
    handleApplyFilter() {
      this.pagination.page = 1;
      this.updateQueryParams();
    },
    handleClearFilter() {
      this.filters.fromDate = null;
      this.filters.toDate = null;
      this.pagination.page = 1;
      this.updateQueryParams();
    },
    async handleExportExcel() {
      if (!this.selectedConnectionId) {
        toast.makeToastFailure('Vui lòng chọn gian hàng trước khi xuất Excel');
        return;
      }
      this.exportingExcel = true;
      try {
        const params = {
          connectionId: this.selectedConnectionId,
        };
        if (this.filters.fromDate) {
          params.fromDate = getStartOfDay(this.filters.fromDate);
        }
        if (this.filters.toDate) {
          params.toDate = getEndOfDay(this.filters.toDate);
        }
        const response = await shopeeAdminRepo.exportOrderReconciliation(params);
        const ctype = String(response.headers['content-type'] || '').toLowerCase();
        if (ctype.includes('application/json')) {
          const text = await response.data.text();
          let msg = 'Xuất Excel thất bại';
          try {
            const j = JSON.parse(text);
            msg = [j.message, j.error, j.msg].find((x) => typeof x === 'string' && x.trim()) || msg;
          } catch (_) {
            /* ignore */
          }
          toast.makeToastFailure(msg);
          return;
        }
        let fileName = `DoiSoatShopee-${moment().format('YYYY-MM-DD_HHmmss')}.xlsx`;
        try {
          fileName = fileDownload.getFileName(response);
        } catch (_) {
          /* default */
        }
        fileDownload.createFile(response.data, fileName);
        toast.makeToastSuccess('Đã tải file Excel');
      } catch (error) {
        console.error('Error exporting reconciliation:', error);
        toast.makeToastFailure('Không thể xuất Excel');
      } finally {
        this.exportingExcel = false;
      }
    },
    onPageChange(page) {
      this.pagination.page = page;
      this.updateQueryParams();
    },
    navigateBack() {
      this.$router.push({ name: 'platform-integration-list' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import './styles/shopee-platform-filter-skin.mixin.scss';

.order-reconciliation-page {
  &,
  * {
    box-sizing: border-box;
  }

  min-width: 0;
  max-width: 100%;

  // Chỉ card KTCodePreview — tránh ảnh hưởng b-card lọc bên trong.
  :deep(.card.card-custom > .card-body) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    min-width: 0;
    max-width: 100%;
  }

  :deep(.card.card-custom) {
    min-width: 0;
    max-width: 100%;
  }

  :deep(.row),
  :deep([class*='col-']) {
    min-width: 0;
    max-width: 100%;
  }

  &__title {
    font-weight: 700;
    color: #181c32;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    max-width: 28rem;
    line-height: 1.4;
  }

  &__back-btn {
    color: #3f4254;
    font-weight: 600;
    border-radius: 8px;
    &:hover {
      background: #f3f6f9 !important;
    }
  }

  &__filter.card {
    @include shopee-platform-admin-filter-card;
    @include shopee-platform-admin-filter-form;

    ::v-deep .card-body.order-reconciliation-page__filter-body {
      padding: 1rem !important;
    }

    ::v-deep .order-reconciliation-page__filter-btn {
      border-radius: 0.28rem;
      font-weight: 600;
      padding: 0.375rem 1rem;
      min-width: 6.25rem;
      box-shadow: none;
    }
  }

  &__empty {
    background: #fff;
    border-radius: 0.42rem;
    box-shadow: none;
    border: 1px dashed #ebedf3;

    .icon-3x {
      font-size: 3rem;
      display: block;
      opacity: 0.45;
    }
  }

  &__summary {
    border-radius: 0.42rem;
    border: 1px solid #e4e6ef;
    box-shadow: 0 1px 3px rgba(24, 28, 50, 0.06);
    background: #fff;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  &__summary-head {
    padding: 0.75rem 1.25rem;
    background: linear-gradient(90deg, #f0f9ff 0%, #f8fafc 100%);
    border-bottom: 1px solid #e4e6ef;
  }

  &__summary-title {
    font-size: 0.8125rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #5e6278;
  }

  &__summary-grid,
  &__summary-voucher-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 1rem 0.75rem 1.25rem;
    gap: 0;

    @media (min-width: 768px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  &__summary-voucher-grid {
    padding-top: 0;
    padding-bottom: 0.75rem;

    @media (min-width: 768px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  &__summary-item {
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #eff2f5;

    &:nth-child(odd) {
      border-right: 1px solid #eff2f5;
    }

    @media (min-width: 768px) {
      border-bottom: none;
      border-right: 1px solid #eff2f5;

      &:last-child {
        border-right: none;
      }
    }
  }

  &__summary-grid &__summary-item {
    @media (min-width: 768px) {
      &:nth-child(4n) {
        border-right: none;
      }
    }

    @media (max-width: 767px) {
      &:nth-last-child(-n + 1):nth-child(odd) {
        border-bottom: none;
      }

      &:nth-last-child(-n + 2):nth-child(even) {
        border-bottom: none;
      }
    }
  }

  &__summary-voucher-grid &__summary-item {
    @media (min-width: 768px) {
      &:nth-child(3n) {
        border-right: none;
      }
    }
  }

  &__summary-label {
    font-size: 0.75rem;
    color: #a1a5b7;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.35rem;
  }

  &__summary-value {
    font-size: clamp(0.8125rem, 1.5vw, 1.05rem);
    font-weight: 700;
    color: #181c32;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.3;

    &--accent {
      color: #0d9488;
    }
  }

  &__total-hint {
    font-variant-numeric: tabular-nums;
  }

  &__results {
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }

  #reconciliation-scroll-table {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0;
    overflow-x: auto !important;
    overflow-y: visible;
    -webkit-overflow-scrolling: touch;
    border-radius: 0.42rem;
    border: 1px solid #e4e6ef;
    box-shadow: 0 1px 3px rgba(24, 28, 50, 0.06);
    background: #fff;

    :deep(.table-responsive) {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      margin-bottom: 0;
      overflow: visible !important;
    }

    :deep(table.reconciliation-table-inner) {
      width: max-content !important;
      min-width: 100%;
      max-width: none;
      margin-bottom: 0;
      table-layout: auto;
      border-collapse: collapse;
    }

    :deep(table.reconciliation-table-inner td) {
      vertical-align: middle;
      font-size: 0.875rem;
      padding-top: 0.65rem;
      padding-bottom: 0.65rem;
    }

    :deep(table.reconciliation-table-inner.table-striped tbody tr:nth-of-type(odd)) {
      background-color: rgba(243, 246, 249, 0.55);
    }

    :deep(table.reconciliation-table-inner.table-hover tbody tr:hover) {
      background-color: rgba(54, 153, 255, 0.06) !important;
    }

    :deep(table.reconciliation-table-inner thead.reconciliation-thead th) {
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.045em;
      color: #7e8299;
      background: #f5f8fa !important;
      border-bottom: 2px solid #e4e6ef !important;
      padding-top: 0.75rem;
      padding-bottom: 0.75rem;
      white-space: nowrap;
      vertical-align: middle;
    }

    :deep(table.reconciliation-table-inner thead.reconciliation-thead th.reconciliation-th--wrap) {
      white-space: normal;
      line-height: 1.25;
    }

    :deep(table.reconciliation-table-inner td.reconciliation-td--wrap),
    :deep(table.reconciliation-table-inner th.reconciliation-th--wrap) {
      white-space: normal;
      word-break: break-word;
      overflow-wrap: anywhere;
    }

    :deep(table.reconciliation-table-inner th.text-right),
    :deep(table.reconciliation-table-inner td.text-right) {
      white-space: nowrap;
    }
  }
}

.order-id-link {
  color: #3699ff;
  text-decoration: none;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  &:hover {
    text-decoration: underline;
    color: #187de4;
  }
}

.reconciliation-line-items {
  display: block;
  max-width: 18rem;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
}

.reconciliation-num {
  font-variant-numeric: tabular-nums;
}

.reconciliation-zero {
  color: #b5b5c3 !important;
  font-weight: 400 !important;
}

.reconciliation-actual:not(.reconciliation-zero) {
  color: #0d9488;
}

:deep(.reconciliation-actual-cell:not(.reconciliation-zero)) {
  color: #0d9488;
  font-weight: 700;
}

</style>
